import { apiNotificationsUserService } from '@api/services/notificationsUser.apiService';
import { INotification } from '@api/services/notificationsUser.apiService.interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { makeAutoObservable, onBecomeObserved, onBecomeUnobserved, runInAction } from 'mobx';
import { MobxRootStore } from './mobx.store';

export class NotificationsStore {
  mobxStore: MobxRootStore;

  private _notifications: INotification[] = [];
  private _pollingInterval: NodeJS.Timeout | undefined;
  private _pollingDelay = 60 * 1000 * 5;
  private _selectedNotificationId: number | null = null;
  private _areNotificationsObserved = false;

  constructor(mobxStore: MobxRootStore) {
    this.mobxStore = mobxStore;
    makeAutoObservable(this);

    document.addEventListener('visibilitychange', () => {
      if (!this._areNotificationsObserved) {
        return;
      }
      if (document.hidden) {
        this.suspendPolling();
      } else {
        void this.resumePolling();
      }
    });

    onBecomeObserved(this, 'notifications', () => {
      this._areNotificationsObserved = true;
      void this.resumePolling();
    });
    onBecomeUnobserved(this, 'notifications', () => {
      this._areNotificationsObserved = false;
      this.suspendPolling();
    });
  }

  resumePolling = async () => {
    await this.fetchNotifications();
    void this.runNotificationPolling();
  };

  suspendPolling = () => {
    clearInterval(this._pollingInterval);
  };

  setAcceptBonusProposal = async (notificationId: number) => {
    const currentNotification = this.notifications.find((notification) => notification.id === notificationId);

    if (!currentNotification || currentNotification.state === 'Accepted') {
      return;
    }

    const statusSnapshot = currentNotification.state;

    try {
      currentNotification.state = 'Accepted';
      currentNotification.isRead = true;

      await apiNotificationsUserService.accept(currentNotification.id);
    } catch (error) {
      currentNotification.state = statusSnapshot;
      handleError(keyMap.sharedErrorsFailedToCreateRecord, error);
    }
  };

  setRejectBonusProposal = async (notificationId: number) => {
    const currentNotification = this.notifications.find((notification) => notification.id === notificationId);

    if (!currentNotification || currentNotification.state !== 'ActionIsRequired') {
      return;
    }

    const statusSnapshot = currentNotification.state;

    try {
      currentNotification.state = 'Rejected';
      currentNotification.isRead = true;

      await apiNotificationsUserService.reject(currentNotification.id);
    } catch (error) {
      currentNotification.state = statusSnapshot;
      handleError(keyMap.sharedErrorsFailedToCreateRecord, error);
    }
  };

  fetchNotifications = async () => {
    try {
      const response = await apiNotificationsUserService.getGrid(
        { skip: 0, take: 999 },
        { methods: ['Popup'], notExpired: true },
      );

      this.updatePollingDelayAndReRunPolling(response.data.pollingIntervalInSeconds);

      runInAction(() => {
        this._notifications = response.data.records.sort((notificationA, notificationB) =>
          new Date(notificationA.expirationDate) > new Date(notificationB.expirationDate) ? 1 : -1,
        );
      });

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  updatePollingDelayAndReRunPolling = (seconds: number) => {
    const newPollingDelay = seconds * 1000;

    if (this._pollingInterval && newPollingDelay !== this._pollingDelay) {
      this._pollingDelay = newPollingDelay;
      void this.runNotificationPolling();
    } else {
      this._pollingDelay = newPollingDelay;
    }
  };

  runNotificationPolling = () => {
    this.suspendPolling();

    this._pollingInterval = setInterval(() => {
      void this.fetchNotifications();
    }, this._pollingDelay);
  };

  setShowSelectedNotificationPopup = (notificationId: number) => {
    this._selectedNotificationId = notificationId;
  };

  setHideSelectedNotificationPopup = () => {
    this._selectedNotificationId = null;
  };

  get notifications() {
    return this._notifications;
  }

  get actualPopupNotifications() {
    return this.notifications.filter((notification) => {
      const isNotExpired = new Date(notification.expirationDate) > new Date();
      const isNotApplied =
        notification.state === 'Accepted' ||
        notification.state === 'ActionIsRequired' ||
        notification.state === 'Rejected';

      return notification.method === 'Popup' && isNotExpired && isNotApplied;
    });
  }

  get selectedNotificationId() {
    return this._selectedNotificationId;
  }

  get selectedNotification() {
    return this.notifications.find((notification) => notification.id === this.selectedNotificationId);
  }

  get unreadPopupNotification() {
    return this.actualPopupNotifications.find((notification) => notification.isRead === false);
  }
}
